import { SwitchHorizontalIcon } from "@heroicons/react/outline";
import { ChevronDown, ChevronRight, ExternalLink, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

import { useContentAreaSize } from "../../../../size-context";
import { getSnippetSizeClasses } from "../../utils";

import VersionLabelSelector from "./VersionLabelSelector";

import { useEditorConfig } from "@/components/ContentArea/editor-config-context";
import { Button } from "@/components/ui/button";
import { EntityIcon } from "@/components/ui/icons";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { EntityType } from "@/queries/folder-entities/types";

interface SnippetHeaderProps {
  name: string;
  version?: number;
  label?: string;
  onEdit?: () => void;
  isCollapsed?: boolean;
  toggleCollapsed?: () => void;
  hasChanged?: boolean;
  onReset?: () => void;
  href?: string;
  isReadOnly?: boolean;
  promptId?: number;
  onVersionLabelChange?: (version: number | undefined, label: string | undefined) => void;
}

const SnippetHeader = ({
  name,
  version,
  label,
  onEdit,
  isCollapsed,
  toggleCollapsed,
  hasChanged = false,
  onReset,
  href,
  isReadOnly = false,
  promptId,
  onVersionLabelChange,
}: SnippetHeaderProps) => {
  const contentAreaSize = useContentAreaSize();

  const { disableInteractive } = useEditorConfig();

  const size = contentAreaSize === "xs" ? "sm" : contentAreaSize;
  const hasSnippet = Boolean(name);

  const getIconSize = () => {
    if (size === "sm") {
      return "md";
    }
    if (size === "md") {
      return "lg";
    }
    return "lg";
  };

  return (
    <div
      className={`flex flex-col items-start justify-between gap-2 transition-all ease-in-out sm:flex-row sm:items-center ${
        isCollapsed || !hasSnippet ? "rounded-md" : "rounded-t-md"
      } ${getSnippetSizeClasses(size, "padding")} ${hasChanged ? "bg-amber-500" : "bg-blue-500"}`}
    >
      {hasSnippet && (
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {toggleCollapsed && !disableInteractive && hasSnippet && (
            <Button
              onClick={e => {
                e.stopPropagation();
                toggleCollapsed();
              }}
              size="tinyIcon"
              variant="ghost"
              className="h-5 w-5 flex-shrink-0 p-0 !text-white"
              aria-label={isCollapsed ? "Expand snippet" : "Collapse snippet"}
            >
              {isCollapsed ? (
                <ChevronRight className={getSnippetSizeClasses(size, "icon")} />
              ) : (
                <ChevronDown className={getSnippetSizeClasses(size, "icon")} />
              )}
            </Button>
          )}
          {hasSnippet && (
            <>
              <div className="flex-shrink-0 text-white">
                <EntityIcon
                  type={EntityType.SNIPPET}
                  size={getIconSize()}
                  className="rounded-full bg-white/20 p-1 !text-white"
                />
              </div>
              <OverflowDisplayTooltip content={name}>
                <span
                  className={`min-w-0 truncate font-mono ${getSnippetSizeClasses(
                    size,
                    "text",
                  )} font-medium text-white`}
                >
                  {name}
                </span>
              </OverflowDisplayTooltip>
              {hasSnippet && (
                <div className="flex-shrink-0">
                  <VersionLabelSelector
                    promptId={promptId}
                    currentVersion={version}
                    currentLabel={label}
                    hasChanged={hasChanged}
                    onChange={onVersionLabelChange}
                    isReadOnly={isReadOnly}
                  />
                </div>
              )}
              {hasChanged && !disableInteractive && (
                <BasicTooltip
                  content="Snippet content has been edited and will propagate to the snippet on template save."
                  variant="black"
                >
                  <span className="ml-1 flex-shrink-0 cursor-help text-white">*</span>
                </BasicTooltip>
              )}
            </>
          )}
          {!hasSnippet && disableInteractive && (
            <span
              className={`font-mono ${getSnippetSizeClasses(size, "text")} font-medium text-white`}
            >
              (No snippet selected)
            </span>
          )}
        </div>
      )}
      <div className="flex w-full items-center justify-end gap-1 sm:w-auto">
        {onEdit && (
          <Button
            onClick={e => {
              e.stopPropagation();
              onEdit();
            }}
            size="xs"
            variant="ghostGray"
            className="flex-shrink-0 gap-1 !text-white"
            aria-label={hasSnippet ? "Change snippet" : "Select snippet"}
          >
            <SwitchHorizontalIcon className={getSnippetSizeClasses(size, "icon")} />
            <span className="hidden md:inline">
              {hasSnippet ? "Change Snippet" : "Select Snippet"}
            </span>
            <span className="md:hidden">{hasSnippet ? "Change" : "Select"}</span>
          </Button>
        )}
        {hasChanged && onReset && !isReadOnly && (
          <Button
            onClick={e => {
              e.stopPropagation();
              onReset();
            }}
            size="xs"
            variant="ghostGray"
            className="flex-shrink-0 gap-1 !text-white"
            aria-label="Reset snippet"
          >
            <RotateCcw className={getSnippetSizeClasses(size, "icon")} />
            <span className="hidden sm:inline">Reset</span>
          </Button>
        )}

        {href && !hasChanged && (
          <Button
            asChild
            size="xs"
            variant="ghostGray"
            className="flex-shrink-0 gap-1 !text-white"
            aria-label="Open snippet"
          >
            <Link
              to={href}
              target={isReadOnly ? undefined : "_blank"}
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink className={getSnippetSizeClasses(size, "icon")} />
              <span className="hidden sm:inline">Open</span>
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default SnippetHeader;
